<template>
  <!--
    `wide` (rich/tabular slot content, e.g. the create-offer unit-mode grid):
    render a hover tooltip teleported to <body> and positioned with fixed
    coordinates clamped to the viewport. As a direct child of <body> it can't
    be clipped by any ancestor's overflow/transform, and the clamp keeps it on
    screen — the failure modes the in-flow popper hit. Every other caller is
    not `wide` and keeps the original vue3-popper text tooltip below, unchanged.
  -->
  <span
    v-if="wide"
    ref="anchorRef"
    class="question-wrapper"
    @mouseenter="openWideTip"
    @mouseleave="scheduleWideTipClose"
  >
    i
  </span>
  <Teleport v-if="wide" to="body">
    <div
      v-if="showWideTip"
      ref="wideTipRef"
      class="ph-grid-tip"
      :style="wideTipStyle"
      @mouseenter="cancelWideTipClose"
      @mouseleave="scheduleWideTipClose"
    >
      <slot />
    </div>
  </Teleport>

  <Popper
    v-else
    trigger="hover"
    arrow
    class="popper"
    :class="{ 'border-fix': fixBorderStyle }"
    :style="{ color: '#212121 !important' }"
    :placement="props.placementDirection"
    :show="localShowPopper"
    :z-index="2"
  >
    <span
      class="question-wrapper"
      @mouseenter="localShowPopper = true"
      @mouseleave="localShowPopper = false"
    >
      i
    </span>
    <template #content>
      <span :style="{ textAlign: 'center', display: 'inline-block' }">{{
        props.textContent
      }}</span>
    </template>
  </Popper>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
// components
import Popper from 'vue3-popper';

// define props
const props = defineProps({
  textContent: {
    type: String,
    default: 'Lorem ipsum dolor sit amet, consectetur adipisicing elit, sed.',
  },
  placementDirection: {
    type: String,
    default: 'right',
    validator(value) {
      // The value must match one of these strings
      return ['right', 'left', 'bottom', 'top'].includes(value);
    },
  },
  inverseStyle: {
    type: Boolean,
    required: false,
    default: false,
  },
  showPopper: {
    type: Boolean,
    default: false,
  },
  fixBorderStyle: {
    type: Boolean,
    default: false,
  },
  // Render the tooltip as a body-teleported, viewport-clamped box instead of
  // the inline vue3-popper. Needed for rich/wide content (a grid) that the
  // narrow inline popper clips. Default keeps the standard text tooltip.
  wide: {
    type: Boolean,
    default: false,
  },
});

const localShowPopper = ref(props.showPopper);
watch(
  () => props.showPopper,
  (newValue) => {
    localShowPopper.value = newValue;
  },
);

// ---- wide (teleported) tooltip positioning ----
const anchorRef = ref(null);
const wideTipRef = ref(null);
const showWideTip = ref(false);
const wideTipStyle = ref({ position: 'fixed', top: '0px', left: '0px' });

// Close on a short delay so the pointer can cross the gap between the icon
// and the teleported box (hovering the box itself cancels the close, matching
// how the inline popper tooltips stay open under the pointer).
let wideTipCloseTimer = null;
function cancelWideTipClose() {
  if (wideTipCloseTimer) {
    clearTimeout(wideTipCloseTimer);
    wideTipCloseTimer = null;
  }
}
function scheduleWideTipClose() {
  cancelWideTipClose();
  wideTipCloseTimer = setTimeout(() => {
    showWideTip.value = false;
    wideTipCloseTimer = null;
  }, 150);
}

async function openWideTip() {
  cancelWideTipClose();
  showWideTip.value = true;
  // Wait for the box to render so we can measure it, then clamp into view.
  await nextTick();
  const anchor = anchorRef.value?.getBoundingClientRect?.();
  const tip = wideTipRef.value?.getBoundingClientRect?.();
  if (!anchor || !tip) return;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Prefer below the icon; flip above if it would run off the bottom.
  let top = anchor.bottom + margin;
  if (top + tip.height > vh - margin) {
    const above = anchor.top - tip.height - margin;
    top = above >= margin ? above : Math.max(margin, vh - tip.height - margin);
  }

  // Left-align to the icon, then clamp so neither edge leaves the viewport.
  let left = anchor.left;
  const maxLeft = vw - tip.width - margin;
  if (left > maxLeft) left = maxLeft;
  if (left < margin) left = margin;

  wideTipStyle.value = {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    zIndex: 1002,
  };
}
</script>

<style scoped lang="scss">
@use '@/scss/theme/variables.scss' as theme;

// Keep tooltip below mobile footer (z-index: 4) and modals (z-index: 1001)
:deep(.inline-block) {
  position: relative;
  z-index: 1;
}

:deep(.popper) {
  background: theme.$white_01 !important;
  padding: 20px !important;
  box-shadow: 1px 1px 5px 1px theme.$blue-100 !important;
  color: theme.$black-102 !important;
  width: 12rem !important;
  padding: 8px !important;
  font-weight: theme.$font-weight-normal !important;
  font-size: theme.$font-size-xs !important;
  border-radius: 2px !important;
  border: 1px theme.$grey-101 solid !important;
  text-align: center;
}

:deep(.popper #arrow) {
  text-align: initial !important;
}

:deep(.popper #arrow::before) {
  background: theme.$white_01;
}

:deep(.popper:hover),
:deep(.popper:hover > #arrow::before) {
  background: theme.$white_01;
}

// Body-teleported wide tooltip. Mirrors the popper's visual style but sizes to
// its content (the grid) and is capped to the viewport so it never clips.
.ph-grid-tip {
  background: theme.$white_01;
  color: theme.$black-102;
  padding: 8px;
  box-shadow: 1px 1px 5px 1px theme.$blue-100;
  border: 1px solid theme.$grey-101;
  border-radius: 2px;
  font-weight: theme.$font-weight-normal;
  font-size: theme.$font-size-xs;
  text-align: left;
  width: max-content;
  max-width: calc(100vw - 16px);
}

.question-wrapper {
  cursor: pointer;
  padding: 0;
  height: 13px;
  width: 13px;
  border-radius: 50%;
  background-color: theme.$grey-301;
  color: theme.$blue-100;
  display: inline-flex;
  font-weight: bold;
  justify-content: center;
  align-items: center;
  line-height: 0;
  font-size: theme.$font-size-xs;
  margin-left: 0.4rem;
  &-inverse {
    background-color: theme.$blue-100;
    color: theme.$grey-301;
    border: none;
  }
}

.border-fix {
  border: none !important;
  display: contents !important;
  span {
    margin-left: 1rem !important;
  }
}
</style>
